import React, { useEffect, useRef, useState } from 'react';

interface FlowStep {
  label: string;
  detail?: string;
}

interface FlowDiagramProps {
  title?: string;
  steps: FlowStep[];
}

export const FlowDiagram: React.FC<FlowDiagramProps> = ({ title, steps }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => { if (ref.current) observer.unobserve(ref.current); };
  }, []);

  return (
    <div ref={ref} className="py-8">
      {title && (
        <h3 className="text-lg font-bold text-slate-900 mb-6 text-center uppercase tracking-wide">{title}</h3>
      )}
      <div className="flex flex-col md:flex-row items-stretch justify-center gap-0">
        {steps.map((step, index) => (
          <div 
            key={index} 
            className={`flex items-center transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: `${index * 200}ms` }}
          >
            <div className="flex flex-col items-center text-center min-w-[140px] md:min-w-[160px]">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm mb-2">
                {String(index + 1).padStart(2, '0')}
              </div>
              <span className="text-sm font-bold text-slate-900 uppercase tracking-wide">{step.label}</span>
              {step.detail && (
                <span className="text-xs text-slate-500 mt-1 max-w-[130px]">{step.detail}</span>
              )}
            </div>
            {index < steps.length - 1 && (
              <div className="hidden md:flex items-center px-2">
                <div className="w-8 h-0.5 bg-orange-500"></div>
                <div className="w-0 h-0 border-t-[5px] border-b-[5px] border-l-[8px] border-transparent border-l-orange-500"></div>
              </div>
            )}
            {index < steps.length - 1 && (
              <div className="flex md:hidden items-center justify-center py-2">
                <div className="h-6 w-0.5 bg-orange-500"></div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
